import { memo, useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';

import { PLAN_TEXT, PLAN_TOOLBAR_ARROW_TAB_HEIGHT } from '../planPageRevampUi';

const TAB_HEIGHT = PLAN_TOOLBAR_ARROW_TAB_HEIGHT;
const ARROW_DEPTH = 10;

type TabDirection = 'left' | 'right';

const getTabPath = (direction: TabDirection, width: number, height: number) => {
  if (direction === 'right') {
    return `M 0.5 0.5 L ${width - ARROW_DEPTH} 0.5 L ${width - 0.5} ${height / 2} L ${
      width - ARROW_DEPTH
    } ${height - 0.5} L 0.5 ${height - 0.5} Z`;
  }

  return `M ${ARROW_DEPTH} 0.5 L ${width - 0.5} 0.5 L ${width - 0.5} ${
    height - 0.5
  } L ${ARROW_DEPTH} ${height - 0.5} L 0.5 ${height / 2} Z`;
};

const getLabelLeftPercent = (direction: TabDirection, width: number) => {
  const bodyCenter =
    direction === 'right' ? (width - ARROW_DEPTH) / 2 : ARROW_DEPTH + (width - ARROW_DEPTH) / 2;
  return (bodyCenter / width) * 100;
};

interface Properties {
  label: string;
  direction?: TabDirection;
  width?: number;
  isActive?: boolean;
  activeBg: string;
  activeColor: string;
  borderColor: string;
  textColor: string;
  onClick: () => void;
  ariaLabel?: string;
}

const PlanToolbarArrowTabButton = ({
  label,
  direction = 'right',
  width = 96,
  isActive = false,
  activeBg,
  activeColor,
  borderColor,
  textColor,
  onClick,
  ariaLabel,
}: Properties) => {
  const tabPath = useMemo(() => getTabPath(direction, width, TAB_HEIGHT), [direction, width]);
  const labelLeft = useMemo(() => getLabelLeftPercent(direction, width), [direction, width]);

  const fill = isActive ? activeBg : 'white';
  const stroke = isActive ? activeColor : borderColor;
  const labelColor = isActive ? activeColor : textColor;
  const labelClass = `plan-toolbar-arrow-tab-${direction}`;

  return (
    <Box
      as='button'
      type='button'
      aria-label={ariaLabel ?? label}
      position='relative'
      w={`${width}px`}
      h={`${TAB_HEIGHT}px`}
      flexShrink={0}
      cursor='pointer'
      bg='transparent'
      border='none'
      p={0}
      onClick={onClick}
      sx={{
        '&:hover path': {
          fill: activeBg,
          stroke: activeColor,
        },
        [`&:hover .${labelClass}`]: {
          color: activeColor,
        },
        '&:active path': {
          fill: activeBg,
        },
      }}
    >
      <svg
        width={width}
        height={TAB_HEIGHT}
        viewBox={`0 0 ${width} ${TAB_HEIGHT}`}
        fill='none'
        aria-hidden
      >
        <path d={tabPath} fill={fill} stroke={stroke} strokeWidth='1' />
      </svg>
      <Text
        className={labelClass}
        position='absolute'
        top='50%'
        left={`${labelLeft}%`}
        transform='translate(-50%, -50%)'
        {...PLAN_TEXT.toolbarTab}
        color={labelColor}
        whiteSpace='nowrap'
        lineHeight='1'
        pointerEvents='none'
      >
        {label}
      </Text>
    </Box>
  );
};

export default memo(PlanToolbarArrowTabButton);
